import type { ReactNode } from 'react'
import {
  highlightLinkedClass,
  highlightRevealToneClass,
  highlightToneClass,
  removedMarkClass,
  type SummaryHighlightSpan,
} from './clinicalSummaryEditorHighlightSpans'
import { revealMarkStaggerMs } from './clinicalSummaryGenerationReveal'

/** Every mark of `code`, or only the one holding `at`. */
export interface LinkedSummaryMark {
  code: string
  at?: number
}

export interface HighlightedSummaryBackdropProps {
  summary: string
  spans: SummaryHighlightSpan[]
  /** Codes whose marks fill in one after another, in reading order. */
  revealCodes?: ReadonlySet<string>
  /** Marks drawn "hover linked". */
  linked?: readonly LinkedSummaryMark[]
}

function isLinked(span: SummaryHighlightSpan, linked: readonly LinkedSummaryMark[]): boolean {
  return linked.some(
    ({ code, at }) => code === span.code && (at === undefined || (span.begin <= at && at < span.end))
  )
}

/** Marks carry `data-begin` so a tooltip can anchor to the line box of the span under the pointer or caret. */
export function HighlightedSummaryBackdrop({ summary, spans, revealCodes, linked = [] }: HighlightedSummaryBackdropProps) {
  if (!summary) return <>{'\u00a0'}</>
  if (spans.length === 0) return <>{summary}</>

  const nodes: ReactNode[] = []
  let cursor = 0
  let revealed = 0
  const staggerMs = revealMarkStaggerMs(revealCodes ? spans.filter((span) => revealCodes.has(span.code)).length : 0)
  spans.forEach((span, index) => {
    const revealing = revealCodes?.has(span.code) ?? false
    const revealStyle = revealing ? { animationDelay: `${Math.round(revealed++ * staggerMs)}ms` } : undefined
    if (span.begin > cursor) {
      nodes.push(summary.slice(cursor, span.begin))
    }
    nodes.push(
      <mark
        key={`${span.code}:${span.begin}:${index}`}
        data-hpo-code={span.code}
        data-begin={span.begin}
        className={
          span.tone === 'removed'
            ? removedMarkClass
            : isLinked(span, linked)
              ? highlightLinkedClass
              : (revealing ? highlightRevealToneClass : highlightToneClass)[span.tone]
        }
        style={revealStyle}
      >
        {summary.slice(span.begin, span.end)}
      </mark>
    )
    cursor = span.end
  })
  if (cursor < summary.length) {
    nodes.push(summary.slice(cursor))
  }
  if (summary.endsWith('\n')) {
    nodes.push('\u200b')
  }
  return <>{nodes}</>
}
